/**
 * Timelines Data Composable
 *
 * Handles data fetching and progress tracking for timelines
 */
import { ref, computed } from '@vue/composition-api';
import { useTimelinesApi } from '../services/timelinesApi';

/**
 * Composable for timelines data management
 * @returns {Object} Data state and methods
 */
export function useTimelinesData() {
  const { fetchTimelines } = useTimelinesApi();

  // Data state
  const timelines = ref([]);
  const storyId = ref('');
  const progress = ref([]);
  const isDemo = ref(false);

  /**
   * Whether the first timeline has been found
   */
  const isFirstTimelineFound = computed(() => {
    return timelines.value.length > 0 && progress.value.includes(timelines.value[0].id);
  });

  /**
   * Visible timelines based on first timeline found status
   * Only show first timeline until it's found, then show all
   */
  const visibleTimelines = computed(() => {
    if (!timelines.value.length) return [];
    return isFirstTimelineFound.value ? timelines.value : timelines.value.slice(0, 1);
  });

  /**
   * Load timelines from API
   * @param {string} groupId - Group ID
   * @param {string} lineId - LINE user ID
   * @returns {Promise<Object>} API response data
   */
  const loadTimelines = async (groupId, lineId) => {
    const data = await fetchTimelines(groupId, lineId);
    timelines.value = data.timelines || [];
    storyId.value = (data.group && data.group.story_id) || '';
    progress.value = data.progress || [];
    isDemo.value = data.demo || false;
    return data;
  };

  /**
   * Check if a timeline is found (in progress)
   * @param {Object} timeline - Timeline to check
   * @returns {boolean} Whether the timeline is found
   */
  const isFound = (timeline) => {
    return progress.value.includes(timeline.id);
  };

  /**
   * Add a timeline to the found list
   * @param {Object} timeline - Timeline that was found
   */
  const markAsFound = (timeline) => {
    if (!progress.value.includes(timeline.id)) {
      progress.value.push(timeline.id);
    }
  };

  /**
   * Get display content for a timeline
   * Shows result if found and unlocked, otherwise shows report
   * @param {Object} timeline - Timeline object
   * @param {boolean} isUnlocked - Whether timeline is unlocked
   * @returns {string} HTML content to display
   */
  const getTimelineContent = (timeline, isUnlocked) => {
    const content = isFound(timeline) && isUnlocked && timeline.result
      ? timeline.result
      : timeline.report;

    if (!content) return '';
    return content.replaceAll('\n', '<br>');
  };

  return {
    // State
    timelines,
    storyId,
    progress,
    isDemo,

    // Computed
    isFirstTimelineFound,
    visibleTimelines,

    // Methods
    loadTimelines,
    isFound,
    markAsFound,
    getTimelineContent
  };
}
